import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { PlusIcon } from '@heroicons/react/24/outline';
import { supabase } from '@/services/supabase';
import { getAccessCodes } from '@/services/api';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import { formatDate } from '@/utils/formatters';
import toast from 'react-hot-toast';

interface AccessCodeRecipient {
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  used_at: string | null;
}

interface AccessCodeBusiness {
  business_id: string;
  business_name: string | null;
  contact_email: string | null;
  phone: string | null;
  business_status: string | null;
}

interface AccessCodeRequest {
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  signup_intent: 'consumer_only' | 'consumer_and_business' | null;
}

interface AccessCode {
  id: string;
  code: string;
  max_uses: number | null;
  current_uses: number;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  status: 'unused' | 'attempted' | 'in_progress' | 'completed';
  associated_email: string | null;
  associated_phone: string | null;
  onboarding_intent: 'consumer' | 'business' | null;
  attempt_count: number;
  attempted_at: string | null;
  recipient: AccessCodeRecipient | null;
  business: AccessCodeBusiness | null;
  request: AccessCodeRequest | null;
}

export default function AccessCodes() {
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [codePrefix, setCodePrefix] = useState('LB6');
  const [maxUses, setMaxUses] = useState('10');
  const [quantity, setQuantity] = useState('1');
  const [expiryDays, setExpiryDays] = useState('30');
  const [onboardingIntent, setOnboardingIntent] = useState<'business' | 'consumer'>('business');

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['accessCodes', page],
    queryFn: () => getAccessCodes(page),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const qty = parseInt(quantity);
      const codes = [];
      const expiry = expiryDays
        ? new Date(Date.now() + parseInt(expiryDays) * 24 * 60 * 60 * 1000).toISOString()
        : null;

      for (let i = 0; i < qty; i++) {
        const randomPart = Math.random().toString(36).substring(2, 8).toUpperCase();
        codes.push({
          id: crypto.randomUUID(),
          code: `${codePrefix}-${randomPart}`,
          max_uses: maxUses ? parseInt(maxUses) : null,
          use_count: 0,
          is_active: true,
          expires_at: expiry,
          onboarding_intent: onboardingIntent,
          created_at: new Date().toISOString(),
        });
      }

      const { error } = await supabase.from('access_codes').insert(codes);
      if (error) throw error;

      return codes;
    },
    onSuccess: (codes) => {
      toast.success(`${codes.length} access code(s) created`);
      queryClient.invalidateQueries({ queryKey: ['accessCodes'] });
      setShowCreateModal(false);
      setCodePrefix('LB6');
      setMaxUses('10');
      setQuantity('1');
      setOnboardingIntent('business');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create codes');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('access_codes')
        .update({ is_active: !isActive })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessCodes'] });
    },
  });

  const getStatusBadge = (code: AccessCode) => {
    // Check completion status first
    if (code.status === 'completed') {
      return <Badge variant="success" dot>Complete</Badge>;
    }
    if (code.status === 'in_progress') {
      return <Badge variant="warning" dot>In Progress</Badge>;
    }
    if (code.status === 'attempted') {
      return <Badge variant="primary" dot>Attempted</Badge>;
    }
    // For unused codes, show active/inactive
    if (!code.is_active) {
      return <Badge variant="gray" dot>Inactive</Badge>;
    }
    // Check expiration
    if (code.expires_at) {
      const expiry = new Date(code.expires_at);
      if (expiry < new Date()) {
        return <Badge variant="danger" dot>Expired</Badge>;
      }
    }
    return <Badge variant="success" dot>Active</Badge>;
  };

  const columns = useMemo<ColumnDef<AccessCode>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => (
          <span className="font-mono text-sm">{row.original.code}</span>
        ),
      },
      {
        id: 'email',
        header: 'Email',
        cell: ({ row }) => {
          const { request, associated_email, recipient } = row.original;
          const email = request?.email || associated_email || recipient?.email;
          if (!email) return <span className="text-gray-400">-</span>;
          return (
            <span className="text-sm text-gray-900">{email}</span>
          );
        },
      },
      {
        id: 'phone',
        header: 'Phone',
        cell: ({ row }) => {
          const { request, associated_phone, recipient } = row.original;
          const phone = request?.phone || associated_phone || recipient?.phone;
          if (!phone) return <span className="text-gray-400">-</span>;
          // Format phone number if it's 10 digits
          const cleaned = phone.replace(/\D/g, '');
          if (cleaned.length === 10) {
            return (
              <span className="text-sm text-gray-900">
                ({cleaned.slice(0, 3)}) {cleaned.slice(3, 6)}-{cleaned.slice(6)}
              </span>
            );
          }
          return <span className="text-sm text-gray-900">{phone}</span>;
        },
      },
      {
        id: 'location',
        header: 'Location',
        cell: ({ row }) => {
          const { request } = row.original;
          if (!request?.city && !request?.state) {
            return <span className="text-gray-400">-</span>;
          }
          return (
            <span className="text-sm text-gray-900">
              {request?.city || ''}{request?.city && request?.state ? ', ' : ''}{request?.state || ''}
            </span>
          );
        },
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => {
          const { request, onboarding_intent } = row.original;
          const intent = request?.signup_intent || onboarding_intent;
          if (!intent) return <span className="text-gray-400">-</span>;
          const isBusinessIntent = intent === 'consumer_and_business' || intent === 'business';
          return (
            <Badge variant={isBusinessIntent ? 'primary' : 'gray'} size="sm">
              {isBusinessIntent ? 'Consumer & Business' : 'Consumer User'}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'current_uses',
        header: 'Uses',
        cell: ({ row }) => {
          const used = row.original.current_uses || 0;
          const max = row.original.max_uses;
          return (
            <span>
              {used} {max ? `/ ${max}` : '/ ∞'}
            </span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => getStatusBadge(row.original),
      },
      {
        accessorKey: 'attempt_count',
        header: 'Attempts',
        cell: ({ row }) => {
          const count = row.original.attempt_count || 0;
          if (count === 0) return <span className="text-gray-400">-</span>;
          return (
            <span className="font-medium text-gray-900" title={row.original.attempted_at ? `Last: ${formatDate(row.original.attempted_at)}` : undefined}>
              {count}
            </span>
          );
        },
      },
      {
        id: 'recipient',
        header: 'Recipient',
        cell: ({ row }) => {
          const { recipient } = row.original;

          // If code has been used and we have recipient info
          if (recipient) {
            return (
              <div className="text-sm">
                <div className="font-medium text-gray-900">
                  {recipient.full_name || 'Unknown'}
                </div>
              </div>
            );
          }

          return <span className="text-gray-400">-</span>;
        },
      },
      {
        id: 'business',
        header: 'Business',
        cell: ({ row }) => {
          const { business } = row.original;

          if (!business) {
            return <span className="text-gray-400">-</span>;
          }

          return (
            <div className="text-sm">
              <div className="font-medium text-gray-900">
                {business.business_name || 'Unnamed'}
              </div>
              <div className="text-gray-500 text-xs">
                {business.contact_email || business.phone || '-'}
              </div>
              {business.business_status && (
                <div className="mt-1">
                  <Badge
                    variant={business.business_status.toLowerCase() === 'active' ? 'success' : 'gray'}
                    size="sm"
                  >
                    {business.business_status}
                  </Badge>
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'expires_at',
        header: 'Expires',
        cell: ({ row }) => {
          if (!row.original.expires_at) return 'Never';
          const expiry = new Date(row.original.expires_at);
          const isExpired = expiry < new Date();
          return (
            <span className={isExpired ? 'text-red-600' : ''}>
              {formatDate(row.original.expires_at)}
              {isExpired && ' (Expired)'}
            </span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => formatDate(row.original.created_at),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          // Don't show toggle for completed codes
          if (row.original.status === 'completed') {
            return null;
          }
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                toggleMutation.mutate({
                  id: row.original.id,
                  isActive: row.original.is_active,
                })
              }
            >
              {row.original.is_active ? 'Deactivate' : 'Activate'}
            </Button>
          );
        },
      },
    ],
    [toggleMutation]
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Access Codes</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage invitation and access codes
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          leftIcon={<PlusIcon className="w-4 h-4" />}
        >
          Create Codes
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Total Codes</p>
            <p className="text-2xl font-bold">{data?.total || 0}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Active Codes</p>
            <p className="text-2xl font-bold">
              {data?.codes.filter((c) => c.is_active && c.status === 'unused').length || 0}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-green-600">
              {data?.codes.filter((c) => c.status === 'completed').length || 0}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">In Progress</p>
            <p className="text-2xl font-bold text-yellow-600">
              {data?.codes.filter((c) => c.status === 'in_progress').length || 0}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Codes table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={data?.codes || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={20}
            totalCount={data?.total}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Generate Access Codes"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="access-code-prefix" className="block text-sm font-medium text-gray-700 mb-1">
              Code Prefix
            </label>
            <input
              id="access-code-prefix"
              name="codePrefix"
              type="text"
              value={codePrefix}
              onChange={(e) => setCodePrefix(e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="LB6"
              maxLength={10}
              aria-label="Code prefix"
            />
          </div>

          <div>
            <label htmlFor="access-code-quantity" className="block text-sm font-medium text-gray-700 mb-1">
              Number of Codes
            </label>
            <input
              id="access-code-quantity"
              name="quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              min={1}
              max={100}
              aria-label="Number of codes"
            />
          </div>

          <div>
            <label htmlFor="access-code-max-uses" className="block text-sm font-medium text-gray-700 mb-1">
              Max Uses per Code
            </label>
            <input
              id="access-code-max-uses"
              name="maxUses"
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="Leave empty for unlimited"
              min={1}
              aria-label="Maximum uses per code"
            />
          </div>

          <div>
            <label htmlFor="access-code-expiry-days" className="block text-sm font-medium text-gray-700 mb-1">
              Expires in (days)
            </label>
            <input
              id="access-code-expiry-days"
              name="expiryDays"
              type="number"
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="Leave empty for no expiry"
              min={1}
              aria-label="Expiration in days"
            />
          </div>

          <div>
            <label htmlFor="access-code-intent" className="block text-sm font-medium text-gray-700 mb-1">
              Onboarding Type
            </label>
            <select
              id="access-code-intent"
              name="onboardingIntent"
              value={onboardingIntent}
              onChange={(e) => setOnboardingIntent(e.target.value as 'business' | 'consumer')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              aria-label="Onboarding type"
            >
              <option value="business">Business</option>
              <option value="consumer">Consumer</option>
            </select>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            loading={createMutation.isPending}
          >
            Generate
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
